import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listClientRequests,
  getClientRequest,
  createClientRequest,
  patchClientRequest,
  deleteClientRequest,
  sendClientRequest,
  cancelClientRequest,
  remindClientRequest,
  getClientRequestItems,
  patchClientRequestItem,
  deleteClientRequestItem,
  listClientRequestTemplates,
} from '../api/clientRequests';

const qk = {
  list: (filters) => ['client-requests', filters],
  detail: (id) => ['client-request', id],
  items: (requestId) => ['client-request-items', requestId],
  templates: () => ['client-request-templates'],
};

function normalizeListParams({ clientId, status, dueAfter, dueBefore } = {}) {
  const params = { page_size: 100 };
  if (clientId) params.client_id = clientId;
  if (status != null && status !== '') {
    if (Array.isArray(status)) {
      params.status = status.filter(Boolean).join(',');
    } else {
      params.status = status;
    }
  }
  if (dueAfter) params.due_after = dueAfter;
  if (dueBefore) params.due_before = dueBefore;
  return params;
}

export function useClientRequestsList(filters = {}) {
  const params = normalizeListParams(filters);
  return useQuery({
    queryKey: qk.list(params),
    queryFn: async () => {
      const res = await listClientRequests(params);
      return res.data;
    },
  });
}

export function useClientRequest(id, options = {}) {
  return useQuery({
    queryKey: qk.detail(id),
    queryFn: async () => {
      const res = await getClientRequest(id);
      return res.data;
    },
    enabled: Boolean(id) && (options.enabled !== false),
  });
}

export function useCreateRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => createClientRequest(payload).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['client-requests'] });
    },
  });
}

export function useUpdateRequest(id) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => patchClientRequest(id, payload).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['client-requests'] });
      qc.invalidateQueries({ queryKey: qk.detail(id) });
    },
  });
}

export function useDeleteRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (requestId) => deleteClientRequest(requestId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['client-requests'] });
    },
  });
}

export function useSendRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (requestId) => sendClientRequest(requestId).then((r) => r.data),
    onSuccess: (_, requestId) => {
      qc.invalidateQueries({ queryKey: ['client-requests'] });
      qc.invalidateQueries({ queryKey: qk.detail(requestId) });
    },
  });
}

export function useCancelRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (requestId) => cancelClientRequest(requestId).then((r) => r.data),
    onSuccess: (_, requestId) => {
      qc.invalidateQueries({ queryKey: ['client-requests'] });
      qc.invalidateQueries({ queryKey: qk.detail(requestId) });
    },
  });
}

export function useRemindRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (requestId) => remindClientRequest(requestId).then((r) => r.data),
    onSuccess: (_, requestId) => {
      qc.invalidateQueries({ queryKey: ['client-requests'] });
      qc.invalidateQueries({ queryKey: qk.detail(requestId) });
    },
  });
}

export function useRequestItems(requestId) {
  return useQuery({
    queryKey: qk.items(requestId),
    queryFn: async () => {
      const res = await getClientRequestItems(requestId);
      return res.data;
    },
    enabled: Boolean(requestId),
  });
}

export function useUpdateItem(requestId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, payload }) =>
      patchClientRequestItem(itemId, payload).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.items(requestId) });
      qc.invalidateQueries({ queryKey: qk.detail(requestId) });
      qc.invalidateQueries({ queryKey: ['client-requests'] });
    },
  });
}

export function useDeleteItem(requestId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (itemId) => deleteClientRequestItem(itemId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.items(requestId) });
      qc.invalidateQueries({ queryKey: qk.detail(requestId) });
      qc.invalidateQueries({ queryKey: ['client-requests'] });
    },
  });
}

export function useRequestTemplates() {
  return useQuery({
    queryKey: qk.templates(),
    queryFn: async () => {
      const res = await listClientRequestTemplates();
      return res.data;
    },
  });
}
